import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Target, CheckSquare, TrendingUp, IdCard, UserCog, LogOut, ChevronLeft, Menu } from 'lucide-react';
import ThemeSwitcher from './ThemeSwitcher';
import NotificationsBell from './NotificationsBell';
import { crmUser } from '../crm/api';

// Mirrors the admin Layout, with the rep's own five screens. Every endpoint
// behind these is already scoped to the logged-in rep by the API.
const NAV = [
  { path: '/employee/home', icon: LayoutDashboard, label: 'Home' },
  { path: '/employee/leads', icon: Target, label: 'My Leads' },
  { path: '/employee/tasks', icon: CheckSquare, label: 'Follow-ups' },
  { path: '/employee/performance', icon: TrendingUp, label: 'My Performance' },
  { path: '/employee/card', icon: IdCard, label: 'My Card' },
  { path: '/employee/profile', icon: UserCog, label: 'My Profile' },
];
const TITLES = {
  '/employee/home': 'Home',
  '/employee/leads': 'My Leads',
  '/employee/tasks': 'Follow-ups',
  '/employee/performance': 'My Performance',
  '/employee/card': 'My Card',
  '/employee/profile': 'My Profile',
};

export default function EmployeeLayout() {
  const [open, setOpen] = useState(true);
  const [drawer, setDrawer] = useState(false); // mobile only
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setDrawer(false); }, [location.pathname]); // close the drawer on navigate
  const me = crmUser() || {};
  const initial = (me.name || 'E').trim().charAt(0).toUpperCase();
  const logout = () => { ['token', 'crm_token', 'crm_user'].forEach((k) => localStorage.removeItem(k)); navigate('/employee/login'); };

  return (
    <div className="flex h-screen" style={{ background: 'radial-gradient(1200px 600px at 100% -10%, var(--bg-a) 0%, var(--bg-b) 45%)' }}>
      {drawer && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setDrawer(false)} aria-hidden="true" />}

      <aside className={`w-[248px] ${open ? 'lg:w-[248px]' : 'lg:w-[82px]'} shrink-0 flex flex-col transition-transform lg:transition-all duration-300 text-white rounded-3xl overflow-hidden
        fixed inset-y-3 left-3 z-40 ${drawer ? 'translate-x-0' : '-translate-x-[115%]'}
        lg:relative lg:inset-auto lg:left-auto lg:translate-x-0 lg:z-auto lg:m-3`}
        style={{ background: 'var(--sidebar)', boxShadow: '0 30px 60px -20px rgba(0,0,0,.5)' }}>
        <div className="pointer-events-none absolute -top-16 -right-10 w-52 h-52 rounded-full" style={{ background: 'radial-gradient(circle,var(--sglow),transparent 70%)' }} />

        <div className="relative h-[70px] flex items-center gap-3 px-5">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center p-1.5 shrink-0 shadow-lg">
            <img src="/tecgs-logo.png" alt="TECGSOFT" className="max-w-full max-h-full object-contain" />
          </div>
          {open && <div className="leading-tight"><div className="font-extrabold text-[16px] tracking-tight">DFC</div><div className="text-[10px] text-white/50 font-medium tracking-wide">LEAD CLOUD</div></div>}
        </div>

        {open && <div className="text-[10px] font-semibold text-white/30 uppercase tracking-wider px-6 mt-3 mb-1">Menu</div>}
        <nav className="relative flex-1 px-3 space-y-1.5 mt-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} title={item.label}
                className={`group relative flex items-center gap-3 px-3.5 py-3 rounded-2xl text-[14px] font-semibold transition-all ${active ? '' : 'text-white/55 hover:text-white'}`}
                style={active ? { background: 'var(--pill)', color: 'var(--pill-text)', boxShadow: '0 8px 20px -6px rgba(0,0,0,.35)' } : {}}>
                {!active && <span className="absolute inset-0 rounded-2xl bg-white/0 group-hover:bg-white/10 transition-colors" />}
                <Icon size={19} className="relative shrink-0" />
                {open && <span className="relative">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="relative p-3">
          {open && (
            <div className="rounded-2xl p-3 mb-2" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.09)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0" style={{ background: 'var(--pill)', color: 'var(--pill-text)' }}>{initial}</div>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold truncate">{me.name || 'Employee'}</div>
                  <div className="text-[10px] text-white/50 truncate">{me.designation || 'Sales'}</div>
                </div>
              </div>
            </div>
          )}
          <button onClick={logout} className="flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-[13px] font-semibold text-white/55 hover:text-white hover:bg-white/10 w-full transition-colors">
            <LogOut size={18} />{open && <span>Sign out</span>}
          </button>
        </div>

        <button onClick={() => setOpen(!open)} aria-label={open ? 'Collapse menu' : 'Expand menu'}
          className="absolute -right-3 top-[62px] w-6 h-6 rounded-full bg-white text-slate-700 shadow-lg hidden lg:flex items-center justify-center border border-slate-200 z-10">
          <ChevronLeft size={15} className={`transition-transform ${open ? '' : 'rotate-180'}`} />
        </button>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-[70px] flex items-center justify-between px-4 lg:px-7 shrink-0 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setDrawer(true)} aria-label="Open menu"
              className="lg:hidden shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border"
              style={{ borderColor: 'var(--border)', background: 'var(--card)', color: 'var(--text)' }}>
              <Menu size={18} />
            </button>
            <div className="min-w-0">
            <h2 className="text-[22px] font-extrabold tracking-tight leading-none" style={{ color: 'var(--heading)' }}>{TITLES[location.pathname] || 'My Portal'}</h2>
            <div className="text-[12px] mt-1 font-medium truncate" style={{ color: 'var(--muted)' }}>TECGSOFT · {me.name || 'Sales'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ThemeSwitcher />
            <NotificationsBell />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-4 lg:px-7 pb-7"><Outlet /></main>
      </div>
    </div>
  );
}
